import { Info, Tooltip } from '@weareopenr/component-library';
import styles from './InfoTooltip.module.scss';

export enum InfoTooltipType {
  HFSS = 'HFSS',
  ENHANCED_BUNDLE = 'ENHANCED_BUNDLE',
  MISSING_POS_ID = 'MISSING_POS_ID',
  MISSING_TAX_RATE = 'MISSING_TAX_RATE',
  TIMETABLE_SNAPSHOT = 'TIMETABLE_SNAPSHOT',
}

export type InfoTooltipVariant = 'info' | 'notifier';

// Add a new entry here to support a new tooltip purpose - no new component
// needed, just a type + its copy.
const INFO_TOOLTIP_CONFIG: Record<InfoTooltipType, { text: string; ariaLabel: string }> = {
  [InfoTooltipType.HFSS]: {
    text: 'This classification affects how the product can be promoted under government HFSS regulations. Openr will share the classification with downstream aggregators when the product is published.',
    ariaLabel: 'High Fat, Salt or Sugar info',
  },
  [InfoTooltipType.ENHANCED_BUNDLE]: {
    text: "Turn this on to flag the item as an enhanced bundle. Supported channels use this to apply the bundle's built-in savings correctly.",
    ariaLabel: 'Enhanced bundle info',
  },
  [InfoTooltipType.MISSING_POS_ID]: {
    text: 'There is no default POS ID set for this product which may lead to publish failures. You can define the POS ID via the product page.',
    ariaLabel: 'Missing default POS ID info',
  },
  [InfoTooltipType.MISSING_TAX_RATE]: {
    text: 'There is no Tax Rate set for this product which may lead to publish failures. You can define the Tax Rate via the product page.',
    ariaLabel: 'Missing tax rate info',
  },
  [InfoTooltipType.TIMETABLE_SNAPSHOT]: {
    text: 'This timetable was published using a snapshot of the menu and pricing data at the time of publishing.',
    ariaLabel: 'Timetable snapshot info',
  },
};

interface IInfoTooltip {
  type: InfoTooltipType;
  /** Overrides the type's default copy - for tooltips that need runtime content, e.g. a date. */
  text?: string;
  /** `notifier` swaps the info glyph for a dot that draws attention to a missing value. */
  variant?: InfoTooltipVariant;
}

export const InfoTooltip = ({ type, text, variant = 'info' }: IInfoTooltip) => {
  const { text: defaultText, ariaLabel } = INFO_TOOLTIP_CONFIG[type];

  return (
    <Tooltip asChild text={text ?? defaultText}>
      <button type="button" className={styles['trigger']} aria-label={ariaLabel}>
        {variant === 'notifier' ? <span className={styles['notifier']} data-testid="notifier-dot" /> : <Info />}
      </button>
    </Tooltip>
  );
};
